import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomInput from "../TextField/TextFeild";
import { closeEditScheduledNotificationModel, updateScheduledNotification } from "../../store/slices/eventSlice";
import TimezoneSelect from "../TimeZoneDropDown/TimezoneSelect";
import moment from "moment";
import { fromRruleString } from "../../helpers/rrule";
import { HUMAN_TIMEZONES } from "../../constants";

const EXCLUDED_NOTIFICATION_GROUPS = new Set(["radiomax", "radiofx", "radiiofx"]);

const getSelectableStationGroups = (stationGroupString) => {
    const groups = (stationGroupString || "")
        .split(",")
        .map((g) => g.trim())
        .filter(Boolean)
        .filter((g) => !EXCLUDED_NOTIFICATION_GROUPS.has(g.toLowerCase()));
    return [...new Set(groups)];
};

const validationSchema = Yup.object({
    title: Yup.string().max(100, 'Subject must be less than 100 characters').required("Subject is required"),
    body: Yup.string().max(255, 'Message must be less than 255 characters').required("Message is required"),
    tz: Yup.string().required("Timezone is required"),
    date: Yup.string().required("Date is required"),
    time: Yup.string()
        .required("Time is required")
        .matches(
            /^(0?[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM)$/,
            "Time must be in the format hh:mm AM/PM"
        ),
});

const getTimezoneLabel = (value) => {
    const entry = Object.entries(HUMAN_TIMEZONES).find(
        ([key, val]) => val === value
    );
    return entry ? entry[0] : null;
};


const renderSchedule = (attributes) => {
    if (!attributes.rrule) return false;

    const getDuration = (value) => {
        if (value == null) { return ''; }
        if (value <= 0) { return ''; }
        const hours = Math.floor(value / 60);
        const minutes = value % 60;
        let hour;
        if (hours === 0) {
            hour = '';
        } else if (hours === 1) {
            hour = `${hours} hr `;
        } else {
            hour = `${hours} hrs `;
        }
        const min = (minutes > 0) ? `${minutes} mins` : '';
        return (hour + min).trim();
    }

    const rules = fromRruleString(attributes.rrule);

    const timezone = attributes?.tz || 'UTC';
    let startTime, startTimeDaily, endTime;

    if (attributes.startDate) {
        const dateWithoutTz = attributes.startDate.replace(/[TZ].*$/, '').replace(/[+\-]\d{2}:\d{2}$/, '');
        const baseMoment = moment.tz(dateWithoutTz, timezone);
        startTime = baseMoment.format('Y-MM-DD hh:mma');
        startTimeDaily = baseMoment.format('hh:mma');
        endTime = baseMoment.add(attributes.duration, 'minutes').format('hh:mma');
    }


    if (rules?.recurring) {
        if (rules?.recurringType === 'daily') {
            return (
                <>
                    <p className="f-11 mb-0">Schedule</p>
                    <p className="f-12"><strong>Daily {startTimeDaily} to {endTime} ({getDuration(attributes.duration)})</strong></p>
                </>
            );
        }
        if (rules?.recurringType === 'weekly') {
            return (
                <>
                    <p className="f-11 mb-0">Schedule</p>
                    <p className="f-12"><strong>Weekly ({rules?.days ? rules?.days.join(', ') : 'none'}) {startTimeDaily} to {endTime} ({getDuration(attributes.duration)})</strong></p>
                </>
            );
        }
    }
    if (!rules?.recurring) {
        return (
            <>
                <p className="f-11 mb-0">Schedule</p>
                <p className="f-12"><strong>{startTime} to {endTime} ({getDuration(attributes.duration)})</strong></p>
            </>

        );
    }
    return null;
}

const EditScheduleNotificationModel = ({ show }) => {
    const dispatch = useDispatch();
    const { event } = useSelector((state) => state.event);
    const user = useSelector((state) => state.auth.user);
    const selectableGroups = getSelectableStationGroups(user?.attributes?.stationGroup);
    const showGroupSelector = selectableGroups.length > 1;
    const [eventData, setEventData] = useState([]);
    const hideModel = () => dispatch(closeEditScheduledNotificationModel());

    const formik = useFormik({
        initialValues: {
            title: "",
            body: "",
            deeplink: "",
            tz: "",
            date: "",
            time: "",
            stationGroups: selectableGroups,
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values) => {
            if (showGroupSelector && (!values.stationGroups || values.stationGroups.length === 0)) {
                formik.setFieldError("stationGroups", "Select at least one StationGroup/App");
                return;
            }
            const payload = { ...values, eventId: event.event_id, notification_id: event.notification_id };
            dispatch(updateScheduledNotification({ message: "Notification updated successfully.", payload }))
                .unwrap()
                .then(() => {
                    hideModel();
                    formik.resetForm();
                });
        },
    });

    const toggleStationGroup = (group) => {
        const current = formik.values.stationGroups || [];
        const next = current.includes(group)
            ? current.filter((g) => g !== group)
            : [...current, group];
        formik.setFieldValue("stationGroups", next);
    };


    useEffect(() => {
        if (event) {
            setEventData(event);
            const eventStationGroups = getSelectableStationGroups(event?.stationGroup);
            const presetStationGroups = eventStationGroups.length ? eventStationGroups : selectableGroups;
            formik.setValues({
                ...formik.values,
                title: event.subject || "",
                body: event.message || "",
                deeplink: event.deeplink || "",
                tz: event.tz ? getTimezoneLabel(event.tz) : "",
                date: event.date ? (() => {
                    const dateWithoutTz = event.date.replace(/[TZ].*$/, '').replace(/[+\-]\d{2}:\d{2}$/, '');
                    return moment.tz(dateWithoutTz, event.tz || 'UTC').format("YYYY-MM-DD");
                })() : "",
                time: event.time || "",
                stationGroups: presetStationGroups,
            });

        }
    }, [event]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            {show && <div className="drawer-overlay" onClick={hideModel}></div>}

            <div className={`drawer ${show ? "open" : ""} overflow-y-auto`}>
                <form className="bg-white h-100 d-flex flex-column" onSubmit={formik.handleSubmit}>

                    <p className="notification_heading m-0 text-center bg-dark text-white">Schedule Notification</p>

                    <div className="form-card p-3 flex-grow-1">

                        {eventData.notification_eventId && (<div>
                            <p className="h7">Event detail:</p>
                            <div className="card p-2">
                                <div style={{ fontSize: '12px' }}>
                                    <p className="f-11 mb-0">{eventData.title}</p>
                                    <p className="f-12"><strong>{eventData.description}</strong></p>


                                    {renderSchedule(eventData)}

                                    {eventData.hosts?.length > 0 && (
                                        <>
                                            <p className="f-11 mb-0">DJs</p>
                                            <p className="f-12">
                                                <strong>{eventData.hosts.join(', ')}</strong>
                                            </p>
                                        </>
                                    )}

                                    {eventData.tags?.length > 0 && (
                                        <>
                                            <p className="f-11 mb-0">Tags</p>
                                            <p className="f-12">
                                                <strong>{eventData.tags.join(', ')}</strong>
                                            </p>
                                        </>
                                    )}

                                </div>
                            </div>
                        </div>)}
                        <CustomInput
                            label="Subject"
                            id="subject"
                            name="title"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.title && formik.errors.title}
                            required
                        />

                        <CustomInput
                            label="Message"
                            id="message"
                            name="body"
                            value={formik.values.body}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.body && formik.errors.body}
                            required
                        />

                        <CustomInput
                            label="Deep Link (optional)"
                            id="deepLink"
                            name="deeplink"
                            value={formik.values.deeplink}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.deeplink && formik.errors.deeplink}
                        />
                        <TimezoneSelect
                            label="Timezone"
                            id="timezone"
                            name="tz"
                            value={formik.values.tz}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.tz && formik.errors.tz}
                        />
                        <CustomInput
                            type="date"
                            label="Date"
                            name="date"
                            value={formik.values.date}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.date && formik.errors.date}
                        />
                        <CustomInput
                            type="time"
                            label="Time"
                            name="time"
                            value={
                                formik.values.time
                                    ? moment(formik.values.time, "hh:mm A").format("HH:mm")
                                    : ""
                            }
                            onChange={(e) => {
                                const value24 = e.target.value; // 14:53
                                const value12 = moment(value24, "HH:mm").format("hh:mm A");

                                formik.setFieldValue("time", value12);
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.time && formik.errors.time}
                        />
                        {showGroupSelector && (
                            <div className="mb-3">
                                <label className="mb-2 fw-semibold">StationGroups / Apps</label>
                                <div className="d-flex flex-column gap-1">
                                    {selectableGroups.map((group) => (
                                        <label key={group} className="d-flex align-items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={(formik.values.stationGroups || []).includes(group)}
                                                onChange={() => toggleStationGroup(group)}
                                            />
                                            <span>{group}</span>
                                        </label>
                                    ))}
                                </div>
                                {formik.errors.stationGroups ? (
                                    <div className="error-text">{formik.errors.stationGroups}</div>
                                ) : null}
                            </div>
                        )}
                    </div>

                    <div className="p-3 text-left border-top">

                        <button type="submit" className="btn btn-lg btn-secondary" disabled={formik.isSubmitting}>
                            {formik.isSubmitting ? "Scheduling..." : "Schedule Notification"}
                        </button>
                        <button type="button" className="btn btn-lg btn-outline-danger mx-3" onClick={hideModel}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default EditScheduleNotificationModel;
